// === IMPORTS ===
import React, { useState, useMemo } from 'react';
import { InteractionTypeDefinition, InteractionCategoryId, AgentStatus, OfficeLayout } from '../types';
import AppIcon from './AppIcon';
import {
  Sparkles, HelpCircle, Plus, Edit2, Trash2, X, Zap, BatteryCharging,
  Droplet, Compass, Clock, MessageSquare, Users, Briefcase, Coffee,
  Info, Layers, DoorOpen, Search,
} from 'lucide-react';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import '../styles/twin-modals-shared.css';
import '../styles/twin-icons-shared.css';
import './InteractionManagerModal.css';

interface InteractionManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  interactions: InteractionTypeDefinition[];
  onSaveInteraction: (interaction: InteractionTypeDefinition) => void;
  onDeleteInteraction: (id: string) => void;
  onSelectForFurniture?: (id: string) => void;
  layout?: OfficeLayout;
  initialTab?: 'catalog' | 'create' | 'guide';
}

// === COMPONENTE: GESTOR DE TIPOS DE INTERACCIÓN ===
export const InteractionManagerModal: React.FC<InteractionManagerModalProps> = ({
  isOpen, onClose, interactions, onSaveInteraction, onDeleteInteraction,
  onSelectForFurniture, layout, initialTab = 'catalog',
}) => {
  const [activeTab, setActiveTab] = useState<'catalog' | 'create' | 'guide'>(initialTab);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [icon, setIcon] = useState<string>('✨');
  const [category, setCategory] = useState<InteractionCategoryId>('productivity');
  const [description, setDescription] = useState<string>('');

  const [primaryStatEffect, setPrimaryStatEffect] = useState<'energy' | 'bladder' | 'focus' | 'task_progress' | 'rest' | 'collaboration'>('energy');
  const [energyDelta, setEnergyDelta] = useState<number>(5.0);
  const [bladderDelta, setBladderDelta] = useState<number>(0.5);
  const [focusDelta, setFocusDelta] = useState<number>(4.0);
  const [durationSec, setDurationSec] = useState<number>(10);
  const [autoTriggerCondition, setAutoTriggerCondition] = useState<string>('Triggers when the agent requirement reaches the activation threshold.');
  const [completionEffect, setCompletionEffect] = useState<string>('Restores agent requirement and returns to assigned workstation.');
  const [agentBubbleStatus, setAgentBubbleStatus] = useState<string>('Interacting...');
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('coffee_break');
  const [isWorkstation, setIsWorkstation] = useState<boolean>(false);
  const [isWalkableDefault, setIsWalkableDefault] = useState<boolean>(false);
  const [interactionToDelete, setInteractionToDelete] = useState<InteractionTypeDefinition | null>(null);

  const filteredInteractions = useMemo(() => {
    return interactions.filter((item) => {
      const matchCat = selectedCategory === 'all' || item.category === selectedCategory;
      const matchQuery = !searchQuery.trim() ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.id.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchQuery;
    });
  }, [interactions, selectedCategory, searchQuery]);

  if (!isOpen) return null;

  const handleStartCreate = () => {
    setEditingId(null);
    setName(''); setIcon('✨'); setCategory('wellness'); setDescription('');
    setPrimaryStatEffect('energy'); setEnergyDelta(6.0); setBladderDelta(0.5); setFocusDelta(4.0);
    setDurationSec(10);
    setAutoTriggerCondition('Triggers when agent energy falls below 30%.');
    setCompletionEffect('Restores energy and agent motivation.');
    setAgentBubbleStatus('✨ Enjoying interaction...');
    setAgentStatus('coffee_break'); setIsWorkstation(false); setIsWalkableDefault(false);
    setActiveTab('create');
  };

  const handleStartEdit = (item: InteractionTypeDefinition) => {
    setEditingId(item.id);
    setName(item.name); setIcon(item.icon); setCategory(item.category); setDescription(item.description);
    setPrimaryStatEffect(item.behavior.primaryStatEffect);
    setEnergyDelta(item.behavior.energyDelta);
    setBladderDelta(item.behavior.bladderDelta);
    setFocusDelta(item.behavior.focusDelta);
    setDurationSec(item.behavior.durationSec);
    setAutoTriggerCondition(item.behavior.autoTriggerCondition);
    setCompletionEffect(item.behavior.completionEffect);
    setAgentBubbleStatus(item.behavior.agentBubbleStatus);
    setAgentStatus(item.behavior.agentStatus);
    setIsWorkstation(Boolean(item.behavior.isWorkstation));
    setIsWalkableDefault(Boolean(item.behavior.isWalkableDefault));
    setActiveTab('create');
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const id = editingId || `custom_int_${name.trim().toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;

    const newDefinition: InteractionTypeDefinition = {
      id,
      name: name.trim(),
      icon: icon.trim() || '✨',
      category,
      description: description.trim() || 'Custom interaction configured for the office.',
      behavior: {
        primaryStatEffect,
        energyDelta: Number(energyDelta),
        bladderDelta: Number(bladderDelta),
        focusDelta: Number(focusDelta),
        durationSec: Number(durationSec),
        autoTriggerCondition: autoTriggerCondition.trim(),
        completionEffect: completionEffect.trim(),
        agentBubbleStatus: agentBubbleStatus.trim() || `${icon} Interacting...`,
        agentStatus,
        isWorkstation,
        isWalkableDefault,
      },
      isDefault: editingId ? interactions.find((i) => i.id === editingId)?.isDefault : false,
    };

    onSaveInteraction(newDefinition);
    setActiveTab('catalog');
  };

  // === RENDER ===
  return (
    <div className="twin-modal-overlay">
      <div className="twin-modal twin-interactionmodal">
        {/* === HEADER === */}
        <div className="twin-interactionmodal-header">
          <div className="twin-interactionmodal-header-left">
            <div className="twin-interactionmodal-icon-badge"><Sparkles className="twin-icon-md" /></div>
            <div>
              <h2 className="twin-interactionmodal-title">
                Interaction Types Manager
                <span className="twin-interactionmodal-title-tag">Simulation Engine</span>
              </h2>
              <p className="twin-interactionmodal-subtitle">Configure behaviors, stat impacts, and rules for office objects</p>
            </div>
          </div>

          <div className="twin-interactionmodal-header-right">
            <div className="twin-interactionmodal-tabs">
              <button type="button" onClick={() => setActiveTab('catalog')} className={`twin-interactionmodal-tab ${activeTab === 'catalog' ? 'twin-interactionmodal-tab--active' : ''}`}>
                <Layers className="twin-icon-sm" /> Catalog ({interactions.length})
              </button>
              <button type="button" onClick={handleStartCreate} className={`twin-interactionmodal-tab ${activeTab === 'create' ? 'twin-interactionmodal-tab--active' : ''}`}>
                <Plus className="twin-icon-sm" /> {editingId ? 'Edit Interaction' : 'New Interaction'}
              </button>
              <button type="button" onClick={() => setActiveTab('guide')} className={`twin-interactionmodal-tab ${activeTab === 'guide' ? 'twin-interactionmodal-tab--active' : ''}`}>
                <HelpCircle className="twin-icon-sm" /> Guide & Parameters
              </button>
            </div>
            <button type="button" onClick={onClose} className="twin-modal-close-btn"><X className="twin-icon-md" /></button>
          </div>
        </div>

        {/* === CONTENIDO === */}
        <div className="twin-interactionmodal-body">
          {/* === TAB 1: CATÁLOGO === */}
          {activeTab === 'catalog' && (
            <div className="twin-interactionmodal-section">
              <div className="twin-interactionmodal-filter-bar">
                <div className="twin-interactionmodal-search-box">
                  <Search className="twin-icon-sm twin-interactionmodal-search-icon" />
                  <input
                    type="text" placeholder="Search interaction by name or effect..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="twin-interactionmodal-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="twin-interactionmodal-search-clear"><X className="twin-icon-sm" /></button>
                  )}
                </div>

                <div className="twin-interactionmodal-category-chips">
                  {[
                    { id: 'all', label: 'All' }, { id: 'productivity', label: 'Productivity' },
                    { id: 'wellness', label: 'Wellness' }, { id: 'hygiene', label: 'Hygiene' },
                    { id: 'collaboration', label: 'Meeting' }, { id: 'logistics', label: 'Logistics' },
                  ].map((cat) => (
                    <button key={cat.id} type="button" onClick={() => setSelectedCategory(cat.id)}
                      className={`twin-interactionmodal-chip ${selectedCategory === cat.id ? 'twin-interactionmodal-chip--active' : ''}`}>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="twin-interactionmodal-cards-grid">
                {filteredInteractions.map((item) => {
                  const b = item.behavior;
                  const isEnergyPositive = b.energyDelta > 0;
                  const isBladderReducing = b.bladderDelta < 0;

                  return (
                    <div key={item.id} className="twin-interactionmodal-card">
                      <div>
                        <div className="twin-interactionmodal-card-header">
                          <div className="twin-interactionmodal-card-header-left">
                            <div className="twin-interactionmodal-card-icon"><AppIcon icon={item.icon} name={item.name} category={item.category} className="twin-icon-md" /></div>
                            <div>
                              <h3 className="twin-interactionmodal-card-title">
                                {item.name}
                                {item.isDefault ? (
                                  <span className="twin-interactionmodal-badge twin-interactionmodal-badge--base">Base</span>
                                ) : (
                                  <span className="twin-interactionmodal-badge twin-interactionmodal-badge--custom">Custom</span>
                                )}
                              </h3>
                              <span className="twin-interactionmodal-card-category">{item.category}</span>
                            </div>
                          </div>

                          <div className="twin-interactionmodal-card-actions">
                            <button type="button" onClick={() => handleStartEdit(item)} title="Edit Parameters" className="twin-interactionmodal-icon-btn">
                              <Edit2 className="twin-icon-sm" />
                            </button>
                            {!item.isDefault && (
                              <button type="button" onClick={() => setInteractionToDelete(item)} title="Delete Interaction" className="twin-interactionmodal-icon-btn twin-interactionmodal-icon-btn--danger">
                                <Trash2 className="twin-icon-sm" />
                              </button>
                            )}
                          </div>
                        </div>

                        <p className="twin-interactionmodal-card-desc">{item.description}</p>

                        <div className="twin-interactionmodal-stat-badges">
                          <div className="twin-interactionmodal-stat-badge">
                            <div className="twin-interactionmodal-stat-badge-label"><BatteryCharging className="twin-icon-xs twin-color-goal" /> Energy</div>
                            <div className={`twin-interactionmodal-stat-badge-value ${isEnergyPositive ? 'twin-stat-positive' : b.energyDelta === 0 ? 'twin-stat-neutral' : 'twin-stat-negative'}`}>
                              {b.energyDelta > 0 ? `+${b.energyDelta}` : b.energyDelta}/s
                            </div>
                          </div>
                          <div className="twin-interactionmodal-stat-badge">
                            <div className="twin-interactionmodal-stat-badge-label"><Droplet className="twin-icon-xs twin-color-cyan" /> Bladder</div>
                            <div className={`twin-interactionmodal-stat-badge-value ${isBladderReducing ? 'twin-stat-positive' : b.bladderDelta === 0 ? 'twin-stat-neutral' : 'twin-stat-amber'}`}>
                              {b.bladderDelta > 0 ? `+${b.bladderDelta}` : b.bladderDelta}/s
                            </div>
                          </div>
                          <div className="twin-interactionmodal-stat-badge">
                            <div className="twin-interactionmodal-stat-badge-label"><Clock className="twin-icon-xs twin-color-purple" /> Duration</div>
                            <div className="twin-interactionmodal-stat-badge-value twin-color-purple">{b.durationSec}s</div>
                          </div>
                        </div>

                        <div className="twin-interactionmodal-rules-box">
                          <div className="twin-interactionmodal-rule-line">
                            <Zap className="twin-icon-xs twin-color-goal" />
                            <span>{b.autoTriggerCondition}</span>
                          </div>
                          <div className="twin-interactionmodal-rule-line">
                            <MessageSquare className="twin-icon-xs twin-color-sky" />
                            <span className="twin-interactionmodal-rule-quote">"{b.agentBubbleStatus}"</span>
                          </div>
                        </div>
                      </div>

                      {onSelectForFurniture && (
                        <button type="button" onClick={() => { onSelectForFurniture(item.id); onClose(); }} className="twin-interactionmodal-select-btn">
                          <Check2 />
                          Select for this Resource
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === TAB 2: CREAR / EDITAR === */}
          {activeTab === 'create' && (
            <form onSubmit={handleSave} className="twin-interactionmodal-form">
              <div className="twin-interactionmodal-form-card">
                <div className="twin-interactionmodal-form-header">
                  <div>
                    <h3 className="twin-interactionmodal-form-title"><Sliders2 />{editingId ? 'Edit Interaction Type' : 'Create New Interaction Type'}</h3>
                    <p className="twin-interactionmodal-form-subtitle">Define behavior parameters governing agent intelligence and physiological needs.</p>
                  </div>
                  {editingId && <span className="twin-interactionmodal-id-tag">ID: {editingId}</span>}
                </div>

                <div className="twin-grid-3">
                  <div>
                    <label className="twin-field-label">Interaction Name <span className="twin-required-star">*</span></label>
                    <input type="text" placeholder="e.g.: Nap Room, Ping Pong Table..." value={name} onChange={(e) => setName(e.target.value)} required className="twin-input twin-input--interaction" />
                  </div>
                  <div>
                    <label className="twin-field-label">Icon / Emoji <span className="twin-required-star">*</span></label>
                    <input type="text" placeholder="e.g.: ☕, 🎮, 🧘" value={icon} onChange={(e) => setIcon(e.target.value)} required maxLength={4} className="twin-input twin-input--interaction twin-text-center" />
                  </div>
                  <div>
                    <label className="twin-field-label">Category</label>
                    <select value={category} onChange={(e) => setCategory(e.target.value as InteractionCategoryId)} className="twin-select twin-input--interaction">
                      <option value="productivity">Productivity</option>
                      <option value="wellness">Wellness / Relaxation</option>
                      <option value="hygiene">Hygiene / Restrooms</option>
                      <option value="collaboration">Collaboration / Meeting</option>
                      <option value="logistics">Logistics / Print</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="twin-field-label">Behavior Description</label>
                  <textarea rows={2} placeholder="Describe what agents do at this resource and why they visit..." value={description} onChange={(e) => setDescription(e.target.value)} className="twin-textarea twin-input--interaction" />
                </div>

                <div className="twin-interactionmodal-impact-panel">
                  <div className="twin-interactionmodal-impact-header">
                    <span className="twin-interactionmodal-impact-title"><Zap className="twin-icon-sm twin-color-goal" /> Simulation Stats Impact</span>
                    <span className="twin-interactionmodal-impact-hint">Adjust how agent stats vary per second of interaction</span>
                  </div>

                  <div className="twin-grid-3">
                    <div>
                      <div className="twin-row-between twin-interactionmodal-slider-label-row">
                        <span className="twin-interactionmodal-slider-label"><BatteryCharging className="twin-icon-xs twin-color-goal" /> Energy Delta (/s)</span>
                        <span className="twin-color-goal twin-agentmenu-flyout-mono-text">{energyDelta > 0 ? `+${energyDelta}` : energyDelta}</span>
                      </div>
                      <input type="range" min="-2" max="15" step="0.5" value={energyDelta} onChange={(e) => setEnergyDelta(parseFloat(e.target.value))} className="twin-range twin-range--amber" />
                      <span className="twin-interactionmodal-slider-hint">{energyDelta > 0 ? 'Recharges energy' : energyDelta < 0 ? 'Consumes energy' : 'Neutral'}</span>
                    </div>

                    <div>
                      <div className="twin-row-between twin-interactionmodal-slider-label-row">
                        <span className="twin-interactionmodal-slider-label"><Droplet className="twin-icon-xs twin-color-cyan" /> Bladder Delta (/s)</span>
                        <span className="twin-color-cyan twin-agentmenu-flyout-mono-text">{bladderDelta > 0 ? `+${bladderDelta}` : bladderDelta}</span>
                      </div>
                      <input type="range" min="-25" max="5" step="0.5" value={bladderDelta} onChange={(e) => setBladderDelta(parseFloat(e.target.value))} className="twin-range twin-range--cyan" />
                      <span className="twin-interactionmodal-slider-hint">{bladderDelta < 0 ? 'Empties bladder (Restroom)' : bladderDelta > 0 ? 'Increases urge to use restroom' : 'Neutral'}</span>
                    </div>

                    <div>
                      <div className="twin-row-between twin-interactionmodal-slider-label-row">
                        <span className="twin-interactionmodal-slider-label"><Compass className="twin-icon-xs twin-color-purple" /> Focus Delta (/s)</span>
                        <span className="twin-color-purple twin-agentmenu-flyout-mono-text">{focusDelta > 0 ? `+${focusDelta}` : focusDelta}</span>
                      </div>
                      <input type="range" min="-2" max="15" step="0.5" value={focusDelta} onChange={(e) => setFocusDelta(parseFloat(e.target.value))} className="twin-range twin-range--purple" />
                      <span className="twin-interactionmodal-slider-hint">Improves agent agility</span>
                    </div>
                  </div>

                  <div>
                    <div className="twin-row-between twin-interactionmodal-slider-label-row">
                      <span className="twin-interactionmodal-slider-label"><Clock className="twin-icon-xs" /> Interaction Duration (Seconds)</span>
                      <span className="twin-agentmenu-flyout-mono-text">{durationSec} sec</span>
                    </div>
                    <input type="range" min="2" max="60" step="1" value={durationSec} onChange={(e) => setDurationSec(parseInt(e.target.value, 10))} className="twin-range twin-range--amber" />
                  </div>
                </div>

                <div className="twin-grid-2">
                  <div>
                    <label className="twin-field-label">Auto-Trigger Condition</label>
                    <input type="text" placeholder="e.g.: Triggers when energy drops below 25%" value={autoTriggerCondition} onChange={(e) => setAutoTriggerCondition(e.target.value)} className="twin-input twin-input--interaction" />
                  </div>
                  <div>
                    <label className="twin-field-label">Agent Speech Bubble Status Message</label>
                    <input type="text" placeholder="e.g.: ☕ Having coffee..., 🧘 Meditating..." value={agentBubbleStatus} onChange={(e) => setAgentBubbleStatus(e.target.value)} className="twin-input twin-input--interaction" />
                  </div>
                </div>

                <div className="twin-interactionmodal-toggles-box">
                  <label className="twin-interactionmodal-toggle-label">
                    <input type="checkbox" checked={isWorkstation} onChange={(e) => setIsWorkstation(e.target.checked)} className="twin-interactionmodal-checkbox" />
                    <div>
                      <div className="twin-interactionmodal-toggle-title">Assignable Workstation</div>
                      <div className="twin-interactionmodal-toggle-hint">Allows an agent to use this resource as a permanent base desk</div>
                    </div>
                  </label>

                  <label className="twin-interactionmodal-toggle-label">
                    <input type="checkbox" checked={isWalkableDefault} onChange={(e) => setIsWalkableDefault(e.target.checked)} className="twin-interactionmodal-checkbox" />
                    <div>
                      <div className="twin-interactionmodal-toggle-title">Walkable by Default</div>
                      <div className="twin-interactionmodal-toggle-hint">Agents can walk across this resource without path obstruction</div>
                    </div>
                  </label>
                </div>

                <div className="twin-interactionmodal-form-footer">
                  <button type="button" onClick={() => setActiveTab('catalog')} className="twin-btn twin-btn--secondary">Cancel</button>
                  <button type="submit" className="twin-interactionmodal-submit-btn">{editingId ? 'Save Changes' : 'Create Interaction'}</button>
                </div>
              </div>
            </form>
          )}

          {/* === TAB 3: GUÍA === */}
          {activeTab === 'guide' && (
            <div className="twin-interactionmodal-guide">
              <div className="twin-interactionmodal-guide-banner">
                <h3 className="twin-interactionmodal-guide-banner-title"><HelpCircle className="twin-icon-md" /> Interaction Engine & Simulation Architecture</h3>
                <p className="twin-interactionmodal-guide-banner-text">
                  In AVAP Digital Twin, each resource or placed object on the layout can act as an <strong className="twin-color-goal-strong">interaction node</strong> with physical boundaries and specific rules. AI agents are autonomous entities continuously evaluating their needs in real time and navigating the office using isometric A* pathfinding.
                </p>
              </div>

              <div className="twin-interactionmodal-guide-cards">
                <div className="twin-interactionmodal-guide-card">
                  <div className="twin-interactionmodal-guide-card-title twin-color-goal"><Briefcase className="twin-icon-sm" /> 1. Interaction Type</div>
                  <p className="twin-interactionmodal-guide-card-text">
                    Identifies the functional purpose of the resource. When an agent interacts with the object, the simulation engine applies the assigned logic (e.g. recharging energy at a coffee machine, emptying bladder in the restroom, or resolving tickets at a workstation).
                  </p>
                  <div className="twin-interactionmodal-guide-example">
                    <strong className="twin-color-goal">Example:</strong> If you place a coffee machine with <code className="twin-color-goal">coffee</code>, agents with low energy will automatically seek out this object on the map.
                  </div>
                </div>

                <div className="twin-interactionmodal-guide-card">
                  <div className="twin-interactionmodal-guide-card-title twin-color-cyan"><Compass className="twin-icon-sm" /> 2. Walkability (isWalkable)</div>
                  <p className="twin-interactionmodal-guide-card-text">Controls physical collision on the isometric grid:</p>
                  <ul className="twin-interactionmodal-guide-list">
                    <li><span className="twin-stat-negative-strong">• Solid / Non-walkable:</span> Agents must route around the resource (tables, desks, walls, machinery).</li>
                    <li><span className="twin-stat-positive-strong">• Walkable:</span> Agents can step freely onto the tile (carpets, open doorways, floor tiles).</li>
                  </ul>
                </div>

                <div className="twin-interactionmodal-guide-card">
                  <div className="twin-interactionmodal-guide-card-title twin-stat-positive-strong"><BatteryCharging className="twin-icon-sm" /> 3. State Variables & Deltas</div>
                  <p className="twin-interactionmodal-guide-card-text">Each agent possesses physiological meters from 0 to 100%:</p>
                  <div className="twin-interactionmodal-guide-defs">
                    <div><strong className="twin-color-goal">⚡ Energy:</strong> Decreases while the agent works and recharges with coffee or breaks.</div>
                    <div><strong className="twin-color-cyan">🚽 Bladder:</strong> Increases over time. If exceeding 80%, the agent halts work to visit a restroom.</div>
                    <div><strong className="twin-color-purple">🧠 Focus:</strong> Influences completion speed for complex tasks.</div>
                  </div>
                </div>

                <div className="twin-interactionmodal-guide-card">
                  <div className="twin-interactionmodal-guide-card-title twin-color-purple"><Clock className="twin-icon-sm" /> 4. Duration & Return Cycle</div>
                  <p className="twin-interactionmodal-guide-card-text">
                    Defines how many seconds the agent remains interacting at the resource. Upon completion or satisfying the need (e.g. energy reaches 100%), the agent computes the shortest path back to their assigned desk and resumes backlog processing.
                  </p>
                </div>
              </div>

              <div className="twin-interactionmodal-guide-card">
                <div className="twin-interactionmodal-guide-card-title twin-color-goal"><Info className="twin-icon-sm" /> Tips for optimal office layout design</div>
                <div className="twin-interactionmodal-tips-grid">
                  <div className="twin-interactionmodal-tip">
                    <span className="twin-interactionmodal-tip-title"><Coffee className="twin-icon-sm twin-color-goal" /> Central Kitchens & Coffee</span>
                    Placing coffee machines near workstations reduces time spent by agents walking during breaks.
                  </div>
                  <div className="twin-interactionmodal-tip">
                    <span className="twin-interactionmodal-tip-title"><DoorOpen className="twin-icon-sm twin-color-cyan" /> Spacious Hallways</span>
                    Maintain walkable corridors of at least 1 tile width between partitions to avoid traffic congestion.
                  </div>
                  <div className="twin-interactionmodal-tip">
                    <span className="twin-interactionmodal-tip-title"><Users className="twin-icon-sm twin-color-purple" /> Meeting Rooms</span>
                    Conference tables channel high-priority collaborative tasks and resolve operational bottlenecks.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* === MODAL DE CONFIRMACIÓN (ya migrado en Grupo B) === */}
      <ConfirmDeleteModal
        isOpen={Boolean(interactionToDelete)}
        onClose={() => setInteractionToDelete(null)}
        onConfirm={() => { if (interactionToDelete) { onDeleteInteraction(interactionToDelete.id); setInteractionToDelete(null); } }}
        title="Delete Interaction?"
        itemName={interactionToDelete?.name}
        itemIcon={interactionToDelete?.icon || 'sparkles'}
        itemType="interaction"
        description={interactionToDelete ? `Are you sure you want to delete the interaction type "${interactionToDelete.name}"? Resources using it will lose this assigned interaction.` : undefined}
        confirmText="Delete Interaction"
      />
    </div>
  );
};

// === PEQUEÑOS HELPERS DE ÍCONO EN LÍNEA (para evitar imports no usados en TS 3.8 con noUnusedLocals) ===
function Check2() { return <span style={{ display: 'inline-flex', marginRight: 4 }}>✓</span>; }
function Sliders2() { return <span style={{ display: 'inline-flex', marginRight: 6 }}>⚙️</span>; }